/**
 * Genera un documento HTML completo con estilos CSS integrados
 * para visualización en iframe
 */
export const buildHtmlDocument = (content: string): string => {
	const styles = getDocumentStyles();

	return `
<!DOCTYPE html>
<html>
<head>
	<meta charset="utf-8">
	<meta name="viewport" content="width=device-width, initial-scale=1.0">
	<style>${styles}</style>
</head>
<body>
	${content}
</body>
</html>`;
};

/**
 * Retorna los estilos CSS completos para el documento
 */
const getDocumentStyles = (): string => {
	return `
		${getBaseStyles()}
		${getTableStyles()}
		${getTypographyStyles()}
		${getTextFormattingStyles()}
		${getMediaStyles()}
	`;
};

/**
 * Estilos base del documento
 */
const getBaseStyles = (): string => `
	body {
		font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif;
		font-size: 14px;
		line-height: 1.6;
		color: var(--text);
		background-color: #fff;
		padding: 16px;
		margin: 0;
		box-sizing: border-box;
	}
	
	* {
		box-sizing: inherit;
	}
`;

/**
 * Estilos para tablas del editor Quill/DevExtreme
 */
const getTableStyles = (): string => `
	table {
		border-collapse: collapse;
		width: 100%;
		margin: 16px 0;
		border: 1px solid #ddd;
		table-layout: auto;
	}
	
	table td,
	table th {
		border: 1px solid #ddd;
		padding: 8px 12px;
		min-width: 50px;
		vertical-align: top;
	}
	
	table th {
		background-color: #f5f5f5;
		font-weight: 600;
		text-align: left;
	}
	
	table tr:nth-child(even) {
		background-color: #fafafa;
	}
	
	table tr:hover {
		background-color: #f0f0f0;
	}
	
	/* Soporte para tablas responsivas */
	@media (max-width: 768px) {
		table {
			font-size: 12px;
		}
		
		table td,
		table th {
			padding: 6px 8px;
		}
	}
`;

/**
 * Estilos de tipografía (encabezados, párrafos, listas)
 */
const getTypographyStyles = (): string => `
	h1, h2, h3, h4, h5, h6 {
		margin-top: 24px;
		margin-bottom: 16px;
		font-weight: 600;
		line-height: 1.25;
		color: var(--text);
	}
	
	h1 { font-size: 2em; }
	h2 { font-size: 1.5em; }
	h3 { font-size: 1.25em; }
	h4 { font-size: 1em; }
	h5 { font-size: 0.875em; }
	h6 { font-size: 0.85em; color: var(--text); }
	
	p {
		margin-bottom: 16px;
		margin-top: 0;
	}
	
	ul, ol {
		padding-left: 2em;
		margin-bottom: 16px;
		margin-top: 0;
	}
	
	li {
		margin-bottom: 4px;
	}
	
	ul ul,
	ul ol,
	ol ul,
	ol ol {
		margin-bottom: 0;
	}
`;

/**
 * Estilos para formato de texto y elementos especiales
 */
const getTextFormattingStyles = (): string => `
	strong, b {
		font-weight: 600;
	}
	
	em, i {
		font-style: italic;
	}
	
	u {
		text-decoration: underline;
	}
	
	s, strike {
		text-decoration: line-through;
	}
	
	a {
		color: #0366d6;
		text-decoration: none;
	}
	
	a:hover {
		text-decoration: underline;
	}
	
	blockquote {
		border-left: 4px solid #dfe2e5;
		padding-left: 16px;
		margin: 16px 0;
		color: #6a737d;
	}
	
	code {
		background-color: #f6f8fa;
		border-radius: 3px;
		padding: 2px 6px;
		font-family: "SFMono-Regular", Consolas, "Liberation Mono", Menlo, monospace;
		font-size: 85%;
	}
	
	pre {
		background-color: #f6f8fa;
		border-radius: 3px;
		padding: 16px;
		overflow: auto;
		margin: 16px 0;
		line-height: 1.45;
	}
	
	pre code {
		background-color: transparent;
		padding: 0;
		border-radius: 0;
	}
	
	hr {
		height: 0.25em;
		padding: 0;
		margin: 24px 0;
		background-color: #e1e4e8;
		border: 0;
	}
`;

/**
 * Estilos para imágenes y contenido multimedia
 */
const getMediaStyles = (): string => `
	img {
		max-width: 100%;
		height: auto;
		display: block;
		margin: 16px 0;
	}
	
	video {
		max-width: 100%;
		height: auto;
		display: block;
		margin: 16px 0;
	}
`;
